import React, { useState } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";

const Container = styled.div`
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 1.5rem;
  padding: 0 1rem;
  box-sizing: border-box;

  @media (min-width: 600px) {
    padding: 0 3rem;
  }
`;

const Card = styled.div`
  background-color: transparent;
  width: 450px;
  height: 500px;
  perspective: 1000px;
  flex: 1 1 300px;
`;

const CardInner = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  transition: transform 0.8s;
  transform-style: preserve-3d;
  cursor: pointer;

  ${Card}:hover & {
    transform: rotateY(180deg);
  }

  &.flipped {
    transform: rotateY(180deg);
  }
`;

const CardFace = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  box-sizing: border-box;
  border-radius: 15px;
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 2rem;
  overflow: hidden;
`;

const Front = styled(CardFace)`
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  color: white;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    border-radius: 15px;
    background: rgba(0, 0, 0, 0.62);
    z-index: 0;
  }

  > * {
    position: relative;
    z-index: 1;
  }
`;

const Back = styled(CardFace)`
  transform: rotateY(180deg);
  -webkit-transform: rotateY(180deg);
  background: #fff;
  text-align: left;
  align-items: flex-start;
  justify-content: flex-start;
  padding: 1.5rem;
`;

const BackTitle = styled.h4`
  font-size: 1.05rem;
  font-weight: 700;
  color: #162b4d;
  margin-bottom: 0.75rem;
  flex-shrink: 0;
`;

const BulletList = styled.ul`
  list-style-type: disc;
  padding-left: 1.4rem;
  margin: 0;
  width: 100%;

  li {
    margin-bottom: 0.45rem;
    font-size: 0.95rem;
    color: #333;
    line-height: 1.4;
  }
`;

const SubBulletList = styled(BulletList)`
  padding-left: 1.2rem;
  margin-top: 0.3rem;

  li {
    font-size: 0.88rem;
    margin-bottom: 0.25rem;
  }
`;

const SmallText = styled.p`
  font-size: 0.92rem;
  color: #555;
  margin: 0.5rem 0 0;
  line-height: 1.4;
`;

const ATLTinkerpreneurFlipCards = () => {
  const { t } = useTranslation();
  const [flippedIndex, setFlippedIndex] = useState(-1);
  const ns = "atl_tinkerpreneur_2026";

  const cards = [
    {
      title: t(`${ns}.section_1_title`),
      image: "/images/tinkerpreneur-2026/how_to_participate.jpg",
      renderBack: () => (
        <>
          <BackTitle>{t(`${ns}.section_1_title`)}</BackTitle>
          <SmallText style={{ marginTop: 0, marginBottom: "0.4rem" }}>
            {t(`${ns}.section_1_content_1`)}
          </SmallText>
          <p
            style={{
              fontSize: "0.95rem",
              fontWeight: 600,
              color: "#162b4d",
              margin: "0.3rem 0 0.2rem",
            }}
          >
            {t(`${ns}.section_1_content_2`)}
          </p>
          <BulletList>
            <li>{t(`${ns}.section_1_content_3`)}</li>
            <li>{t(`${ns}.section_1_content_4`)}</li>
            <li>{t(`${ns}.section_1_content_5`)}</li>
          </BulletList>
          <SmallText>{t(`${ns}.section_1_content_6`)}</SmallText>
        </>
      ),
    },
    {
      title: t(`${ns}.section_2_title`),
      image: "/images/tinkerpreneur-2026/who_can-participate.jpeg",
      renderBack: () => (
        <>
          <BackTitle>{t(`${ns}.section_2_title`)}</BackTitle>
          <BulletList>
            <li>
              {t(`${ns}.section_2_content_1`)}{" "}
              <a
                href="https://aistudent.community/login"
                target="_blank"
                rel="noreferrer"
                style={{ color: "#0048ff" }}
              >
                (aistudent.community/login)
              </a>
            </li>
            <li>{t(`${ns}.section_2_content_2`)}</li>
            <li>
              {t(`${ns}.section_2_content_3`)}
              <SubBulletList>
                <li>{t(`${ns}.section_2_content_3_sub`)}</li>
              </SubBulletList>
            </li>
            <li>{t(`${ns}.section_2_content_4`)}</li>
            <li>{t(`${ns}.section_2_content_5`)}</li>
            <li>{t(`${ns}.section_2_content_6`)}</li>
            <li>{t(`${ns}.section_2_content_7`)}</li>
            <li>{t(`${ns}.section_2_content_8`)}</li>
          </BulletList>
        </>
      ),
    },
    {
      title: t(`${ns}.section_3_title`),
      image: "/images/tinkerpreneur-2026/why_to_participate.jpg",
      renderBack: () => (
        <>
          <BackTitle>{t(`${ns}.section_3_title`)}</BackTitle>
          <BulletList>
            <li>{t(`${ns}.section_3_content_1`)}</li>
            <li>{t(`${ns}.section_3_content_2`)}</li>
            <li>{t(`${ns}.section_3_content_3`)}</li>
            <li>{t(`${ns}.section_3_content_4`)}</li>
            <li>{t(`${ns}.section_3_content_5`)}</li>
          </BulletList>
        </>
      ),
    },
  ];

  return (
    <Container>
      {cards.map((card, index) => (
        <Card key={index}>
          <CardInner
            className={flippedIndex === index ? "flipped" : ""}
            onClick={() => setFlippedIndex(flippedIndex === index ? -1 : index)}
          >
            <Front style={{ backgroundImage: `url("${card.image}")` }}>
              <h2
                style={{
                  fontWeight: 700,
                  fontSize: "1.85rem",
                  textAlign: "center",
                  color: "#fff",
                }}
              >
                {card.title}
              </h2>
            </Front>
            <Back>{card.renderBack()}</Back>
          </CardInner>
        </Card>
      ))}
    </Container>
  );
};

export default ATLTinkerpreneurFlipCards;
